import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";

export const Hero = ({ hero, property }) => {
  const [slide, setSlide] = useState(0);
  const reduceMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 220]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % hero.slides.length), 6000);
    return () => clearInterval(t);
  }, [hero.slides.length]);

  return (
    <section className="relative h-[92vh] min-h-[560px] overflow-hidden" aria-label="Welcome to Pebble Farms" data-testid="hero-section">
      <motion.div style={reduceMotion ? {} : { y }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={hero.slides[slide].url}
            alt={hero.slides[slide].alt}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-slide"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/30 to-charcoal/25" aria-hidden />

      <motion.div style={reduceMotion ? {} : { opacity }} className="absolute inset-0 flex items-end">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16 sm:pb-24 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="text-cream/85 uppercase tracking-[0.28em] text-xs sm:text-sm font-semibold mb-4"
            data-testid="hero-eyebrow"
          >
            <MapPin size={13} className="inline -mt-0.5 mr-1.5" aria-hidden />{hero.eyebrow}
          </motion.p>
          <h1 className="font-serif font-semibold text-cream leading-[0.98]" data-testid="hero-headline">
            <span className="mask-line">
              <motion.span className="block text-5xl sm:text-7xl lg:text-8xl"
                initial={reduceMotion ? false : { y: "112%" }} animate={{ y: 0 }}
                transition={{ delay: 0.45, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
                Pebble
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="block text-5xl sm:text-7xl lg:text-8xl italic text-[#EBC9A8]"
                initial={reduceMotion ? false : { y: "112%" }} animate={{ y: 0 }}
                transition={{ delay: 0.62, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
                Farms
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="text-cream/90 text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            {hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.7 }}
            className="flex flex-wrap gap-3 mt-8"
          >
            <a href={property.booking_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center bg-copper text-cream font-semibold rounded-full px-8 py-4 text-lg hover:bg-copperdeep hover:scale-[1.03] transition-all shadow-lg"
              data-testid="hero-book-now-button">
              {hero.primary_cta}
            </a>
            <a href="#availability"
              className="inline-flex items-center border-2 border-cream/50 text-cream font-semibold rounded-full px-8 py-4 text-lg hover:bg-cream/10 hover:border-cream transition-all"
              data-testid="hero-check-availability-button">
              {hero.secondary_cta}
            </a>
            <a href="/login"
              className="inline-flex items-center text-cream/90 font-semibold rounded-full px-6 py-4 text-lg underline underline-offset-4 decoration-cream/40 hover:decoration-cream hover:text-cream transition-all"
              data-testid="hero-guide-login-button">
              Access Your Welcome Guide
            </a>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" aria-hidden>
        <div className="flex gap-1.5">
          {hero.slides.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === slide ? "w-6 bg-cream" : "w-1.5 bg-cream/40"}`} />
          ))}
        </div>
        <ChevronDown size={18} className="text-cream/70 animate-bounce" />
      </div>
    </section>
  );
};
